import axios from 'axios'
import { decryptData, encryptData } from './tweetnacl'

export const getCookie = (name) => {
    if (typeof document === 'undefined') return ''

    const prefix = `${encodeURIComponent(name)}=`
    const cookie = document.cookie
        .split('; ')
        .find((value) => value.startsWith(prefix))

    return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : ''
}

// 'getDefault' loads public bootstrap config (categories, locations, feature
// flags, translations, etc.) and must succeed before a user is authenticated -
// e.g. to render the login page itself. 'validateUser' is the bootstrap
// "am I already logged in?" check run on every app load, before it's known
// whether a session exists. Neither mutates data, so both are safe to exempt
// from the CSRF guard the same way 'login' is, otherwise any visitor without
// an existing XSRF-TOKEN cookie (first visit, cleared cookies, or right after
// logout, which clears XSRF-TOKEN too) gets permanently blocked from loading
// the app at all.
const csrfExemptEndpoints = new Set([
    'login',
    'get2faDetails',
    'getDefault',
    'validateUser',
    'forgotAdminPassword',
    'resetAdminPassword',
])
const activeRequests = new Set()
let sessionTimeoutLogoutDone = false
let sessionTimeoutHandler = null

const resetSessionTimeoutLogout = () => {
    sessionTimeoutLogoutDone = false
}

const setSessionTimeoutHandler = (handler) => {
    sessionTimeoutHandler = handler
}

const logoutOnSessionTimeout = (responseData, url) => {
    if (
        sessionTimeoutLogoutDone ||
        responseData?.status !== 'error' ||
        responseData?.data?.responseType !== 403 ||
        responseData?.data?.message !== 'Session timed out!' ||
        url === 'logout' ||
        typeof sessionTimeoutHandler !== 'function'
    ) {
        return
    }

    sessionTimeoutLogoutDone = true
    sessionTimeoutHandler()
}

const clearAllApiRequests = () => {
    if (!global?.zb_session_timeout_ft) return
    activeRequests.forEach((source) =>
        source.cancel('Request was manually cleared/canceled.')
    )
    activeRequests.clear()
    console.log('[API] All pending network calls cleared.')
}

const apiCall = async (
    method,
    url,
    data,
    headertype,
    baseurl,
    common,
    custom,
    front
) => {
    const credentialedCorsEnabled =
        process.env.REACT_APP_CREDENTIALED_CORS !== undefined
            ? process.env.REACT_APP_CREDENTIALED_CORS === 'true'
            : global.credentialed_cors === true
    if (credentialedCorsEnabled) {
        const requestMethod = String(method || '').toLowerCase()
        const isStateChanging = !['get', 'head', 'options'].includes(
            requestMethod
        )

        if (global.decryptData && data) {
            data = data instanceof FormData ? data : encryptData(data)
        }
        const apiOrigin = global.site_url
        let site_url = `${apiOrigin}/api/admin/`
        if (baseurl) {
            site_url = `${apiOrigin}/api/admin/${baseurl}/`
        }

        if (common) {
            site_url = `${apiOrigin}/api/commonFunction/`
            if (baseurl) {
                site_url = `${apiOrigin}/api/commonFunction/${baseurl}/`
            }
        }

        if (custom) {
            site_url = `${apiOrigin}/api/custom/`
            if (baseurl) {
                site_url = `${apiOrigin}/api/custom/${baseurl}/`
            }
        }

        if (front) {
            site_url = `${apiOrigin}/api/`
            if (baseurl) {
                site_url = `${apiOrigin}/api/${baseurl}/`
            }
        }

        // A FormData body must never get an explicit multipart Content-Type here -
        // axios/the browser needs to set it themselves so the multipart boundary is
        // included, or the server can't parse the request as multipart at all (it
        // silently drops the files instead of erroring, which is easy to miss).
        const isFormData = data instanceof FormData
        let headers = {}
        if (!isFormData) {
            headers['content-type'] =
                headertype && headertype === 'formdata'
                    ? 'multipart/form-data'
                    : 'application/json'
        }

        const csrfToken = getCookie('XSRF-TOKEN') || getCookie('csrfToken')
        const isCsrfExempt = csrfExemptEndpoints.has(
            String(url || '').split('?')[0]
        )
        const hasLegacyBearerToken = Boolean(
            axios.defaults.headers.common['Authorization'] ||
            axios.defaults.headers.common['authorization']
        )
        if (
            isStateChanging &&
            !csrfToken &&
            !isCsrfExempt &&
            !hasLegacyBearerToken
        ) {
            console.error('[API Security] Missing CSRF token; request blocked')
            return {
                data: {
                    status: 'error',
                    data: {
                        message:
                            'Security validation failed. Refresh the page and try again.',
                        statusCode: 403,
                    },
                },
            }
        }

        if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken
        }

        const config = {
            headers,
            withCredentials: true,
            xsrfCookieName: 'XSRF-TOKEN',
            xsrfHeaderName: 'X-CSRF-Token',
        }

        try {
            let res
            if (requestMethod === 'post') {
                res = await axios.post(`${site_url}${url}`, data || {}, config)
            } else if (requestMethod === 'get') {
                res = await axios.get(`${site_url}${url}`, config)
            } else {
                throw new Error(`Unsupported method: ${method}`)
            }

            if (global.decryptData && res.data) {
                res.data =
                    data instanceof FormData ? res.data : decryptData(res.data)
            }

            logoutOnSessionTimeout(res.data, url)
            return res
        } catch (err) {
            console.error(
                '[API Error] Request failed',
                err?.response?.status || 'network'
            )

            if (axios.isCancel(err) || err.name === 'CanceledError') {
                return {
                    data: {
                        status: 'canceled',
                        data: {
                            message: 'Request was manually cleared/canceled.',
                        },
                    },
                }
            }

            if (err.code === 'ECONNABORTED') {
                return {
                    data: {
                        status: 'error',
                        data: {
                            message:
                                'Request timed out. Please try again later.',
                        },
                    },
                }
            }

            if (err.response) {
                let respData = err.response.data
                if (global.decryptData && respData) {
                    respData = decryptData(respData)
                }
                logoutOnSessionTimeout(respData, url)
                return {
                    data: {
                        status: 'error',
                        data: {
                            message: respData?.message || 'Server Error',
                            statusCode: err.response.status,
                        },
                    },
                }
            }

            return {
                data: {
                    status: 'error',
                    data: {
                        message:
                            'Unable to complete the request. Please try again.',
                    },
                },
            }
        }
    } else {
        if (global.decryptData && data) {
            data = data instanceof FormData ? data : encryptData(data)
        }
        let site_url = `${global.site_url}/api/admin/`
        if (baseurl) {
            site_url = `${global.site_url}/api/admin/${baseurl}/`
        }
        if (common) {
            site_url = `${global.site_url}/api/commonFunction/`
            if (baseurl) {
                site_url = `${global.site_url}/api/commonFunction/${baseurl}/`
            }
        }
        if (custom) {
            site_url = `${global.site_url}/api/custom/`
            if (baseurl) {
                site_url = `${global.site_url}/api/custom/${baseurl}/`
            }
        }
        if (front) {
            site_url = `${global.site_url}/api/`
            if (baseurl) {
                site_url = `${global.site_url}/api/${baseurl}/`
            }
        }

        const headers = {}
        if (!(data instanceof FormData)) {
            headers['content-type'] =
                headertype === 'formdata'
                    ? 'multipart/form-data'
                    : 'application/json'
        }

        // Axios 0.21 does not support AbortController signals.
        const cancellation = global?.zb_session_timeout_ft
            ? axios.CancelToken.source()
            : null
        const config = {
            headers,
            ...(cancellation ? { cancelToken: cancellation.token } : {}),
        }
        if (cancellation) {
            activeRequests.add(cancellation)
        }

        try {
            let res
            if (method === 'post') {
                res = await axios.post(`${site_url}${url}`, data || {}, config)
            } else if (method === 'get') {
                res = await axios.get(`${site_url}${url}`, config)
            } else {
                throw new Error(`Unsupported method: ${method}`)
            }
            if (global.decryptData && res.data) {
                res.data =
                    data instanceof FormData ? res.data : decryptData(res.data)
            }
            if (res?.data?.data?.responseType === 403 && cancellation) {
                activeRequests.delete(cancellation)
                clearAllApiRequests()
            }
            logoutOnSessionTimeout(res.data, url)
            return res
        } catch (err) {
            console.error('API Error:', err?.message || err)
            if (axios.isCancel(err) || err.name === 'CanceledError') {
                return {
                    data: {
                        status: 'canceled',
                        data: {
                            message: 'Request was manually cleared/canceled.',
                        },
                    },
                }
            }
            if (err.code === 'ECONNABORTED') {
                return {
                    data: {
                        status: 'error',
                        data: {
                            message:
                                'Request timed out. Please try again later.',
                        },
                    },
                }
            }
            if (err.response) {
                let respData = err.response.data
                if (global.decryptData && respData) {
                    respData = decryptData(respData)
                }
                logoutOnSessionTimeout(respData, url)
                return {
                    data: {
                        status: 'error',
                        data: {
                            message: respData?.message || 'Server Error',
                            statusCode: err.response.status,
                        },
                    },
                }
            }
            return {
                data: {
                    status: 'error',
                    data: {
                        message: err.message || 'Something went wrong!',
                    },
                },
            }
        } finally {
            if (cancellation) {
                activeRequests.delete(cancellation)
            }
        }
    }
}

const setIPAddress = (ipaddress) => {
    if (ipaddress) {
        axios.defaults.headers.common['ipaddress'] = ipaddress
    } else {
        delete axios.defaults.headers.common['ipaddress']
    }
}

// Legacy auth mode (httponly_cookie_auth flag OFF): the JWT travels via this
// header instead of the HttpOnly auth_token cookie, since there is no cookie
// to rely on. Unused (never called) while cookie-auth is active.
const setAuthToken = (token) => {
    delete axios.defaults.headers.common['authorization']
    if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
        delete axios.defaults.headers.common['Authorization']
    }
}

export {
    apiCall,
    setIPAddress,
    setAuthToken,
    resetSessionTimeoutLogout,
    setSessionTimeoutHandler,
}
